// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/';
import { DashboardDropWidgetResult, WIDGET_DND_TYPES } from '@Types';
import React, { ReactElement, useRef, ReactNode, useEffect, useState } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import EmptyElement from '@Organisms/Dashboard/DashboardDetails/EmptyElementToDrop/EmptyElementToDrop';

import './DropableWidgetView.scss';

interface DropableWidgetProps {
    children: ReactNode;
    rowIndex: number;
    widgetIndex: number;
    onDragFinish: (dropResult: DashboardDropWidgetResult) => void;
    canAddWidget: boolean;
}

const DropableWidgetView = ({
    children,
    widgetIndex,
    rowIndex,
    onDragFinish,
    canAddWidget,
}: DropableWidgetProps): ReactElement => {
    const ref = useRef(null);
    const i = useLocalMessage();
    const [isHovered, setIsHovered] = useState<boolean>(false);

    const [{ isHoveredInit, canDrop }, drop] = useDrop({
        accept: WIDGET_DND_TYPES.WIDGET,
        collect: (monitor) => ({
            isHoveredInit: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
        canDrop: (item: any) => canAddWidget || item?.draggedRowIndex === rowIndex,
        drop: () => {
            return { hoveredWidgetIndex: widgetIndex };
        },
    });

    const [{ isDragging }, drag] = useDrag({
        item: { type: WIDGET_DND_TYPES.WIDGET, draggedRowIndex: rowIndex, draggedWidgetIndex: widgetIndex },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            const dropResult = monitor.getDropResult();

            if (dropResult) {
                onDragFinish?.(dropResult);
            }
        },
    });

    useEffect(() => {
        setIsHovered(isHoveredInit);
    }, [isHoveredInit]);

    drag(drop(ref));

    const shouldDisplayEmptyBlock = isHovered && canDrop;

    return (
        <div
            ref={ref}
            className={`dropable-widget ${isDragging ? 'dragged' : ''} ${shouldDisplayEmptyBlock ? 'hovered' : ''}`}
        >
            {!isDragging && (
                <>
                    {shouldDisplayEmptyBlock && <EmptyElement text={i('dashboard.drop.widget')} />}
                    {children}
                </>
            )}
        </div>
    );
};

export default DropableWidgetView;
